import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { supabase } from '../supabase/client';

interface AdminUser {
  id: string;
  username: string;
  email: string;
  role: 'super' | 'normal';
  createdAt: string;
}

interface AuthContextType {
  user: AdminUser | null;
  isAuthenticated: boolean;
  isSuperAdmin: boolean;
  login: (username: string, password: string) => Promise<boolean>;
  logout: () => void;
  admins: AdminUser[];
  addAdmin: (user: Omit<AdminUser, 'id' | 'createdAt'>, password: string) => Promise<void>;
  removeAdmin: (id: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AdminUser | null>(null);
  const [admins, setAdmins] = useState<AdminUser[]>([]);

  useEffect(() => {
    loadAdmins();
  }, []);

  const loadAdmins = async () => {
    const { data, error } = await supabase
      .from('admin_users')
      .select('*')
      .order('created_at', { ascending: false });
    if (!error && data) {
      setAdmins((data as Array<{ id: string; username: string; email?: string; role: string; created_at: string }>).map(a => ({
        id: a.id,
        username: a.username,
        email: a.email || '',
        role: a.role === 'super' ? 'super' : 'normal',
        createdAt: a.created_at
      })));
    }
  };

  const login = useCallback(async (username: string, password: string): Promise<boolean> => {
    const { data, error } = await supabase
      .from('admin_users')
      .select('*')
      .eq('username', username)
      .eq('status', 'active')
      .maybeSingle();

    if (error || !data) return false;

    const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
      email: `${username}@admin.local`,
      password
    });

    if (authError || !authData.user) return false;

    setUser({
      id: (data as { id: string; username: string; email?: string; role: string; created_at: string }).id,
      username: (data as { id: string; username: string; email?: string; role: string; created_at: string }).username,
      email: (data as { id: string; username: string; email?: string; role: string; created_at: string }).email || '',
      role: (data as { id: string; username: string; email?: string; role: string; created_at: string }).role === 'super' ? 'super' : 'normal',
      createdAt: (data as { id: string; username: string; email?: string; role: string; created_at: string }).created_at
    });
    return true;
  }, []);

  const logout = useCallback(() => {
    supabase.auth.signOut();
    setUser(null);
  }, []);

  const addAdmin = useCallback(async (newUser: Omit<AdminUser, 'id' | 'createdAt'>, password: string) => {
    const { error: authError } = await supabase.auth.signUp({
      email: `${newUser.username}@admin.local`,
      password,
      options: { data: { username: newUser.username } }
    });

    if (authError) throw authError;

    const { error } = await supabase.from('admin_users').insert({
      username: newUser.username,
      email: newUser.email,
      phone: '',
      role: newUser.role,
      status: 'active'
    });

    if (error) throw error;
    await loadAdmins();
  }, []);

  const removeAdmin = useCallback(async (id: string) => {
    const { error } = await supabase
      .from('admin_users')
      .delete()
      .eq('id', id)
      .neq('role', 'super');

    if (error) throw error;
    await loadAdmins();
  }, []);

  const isSuperAdmin = user?.role === 'super';

  return (
    <AuthContext.Provider value={{
      user,
      isAuthenticated: !!user,
      isSuperAdmin,
      login,
      logout,
      admins,
      addAdmin,
      removeAdmin
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
